import { Upload, Image, X } from "lucide-react";
import React, { useCallback, useRef, useState } from "react";
import { FileMetaData } from "./onboarding-schema";

interface FileDropZoneProps {
  accept: string;
  maxSizeBytes: number;
  multiple: boolean;
  icon?: "upload" | "image";
  label: string;
  hint: string;
  files: FileMetaData[];
  onFilesChange: (files: FileMetaData[]) => void;
  showPreviews?: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const FileDropZone = ({
  accept,
  maxSizeBytes,
  multiple,
  icon = "upload",
  label,
  hint,
  files,
  onFilesChange,
  showPreviews = true,
}: FileDropZoneProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const acceptTypes = accept.split(",").map((t) => t.trim());

  const processFiles = useCallback(
    (incoming: FileList) => {
      setError(null);
      const valid: FileMetaData[] = [];

      for (let i = 0; i < incoming.length; i++) {
        const file = incoming[i];

        if (!acceptTypes.some((t) => file.type.match(t.replace("*", ".*")))) {
          setError(`Dateityp "${file.name}" wird nicht unterstützt.`);
          continue;
        }

        if (file.size > maxSizeBytes) {
          setError(
            `"${file.name}" ist zu groß (max. ${formatFileSize(maxSizeBytes)}).`
          );
          continue;
        }

        valid.push({
          name: file.name,
          size: file.size,
          type: file.type,
          previewUrl: URL.createObjectURL(file),
        });
      }

      if (valid.length === 0) return;

      if (multiple) {
        onFilesChange([...files, ...valid]);
      } else {
        onFilesChange(valid.slice(0, 1));
      }
    },
    [acceptTypes, files, maxSizeBytes, multiple, onFilesChange]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(true);
    },
    []
  );

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        processFiles(e.dataTransfer.files);
      }
    },
    [processFiles]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        processFiles(e.target.files);
      }
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    },
    [processFiles]
  );

  const handleRemove = useCallback(
    (index: number) => {
      const updated = files.filter((_, i) => i !== index);
      onFilesChange(updated);
    },
    [files, onFilesChange]
  );

  const handleClick = () => {
    inputRef.current?.click();
  };

  const IconComponent = icon === "image" ? Image : Upload;

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleClick();
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed
          py-10 px-6 cursor-pointer transition-colors
          ${
            isDragging
              ? "border-[#0099ce] bg-[rgba(0,153,206,0.05)]"
              : "border-gray-300 hover:border-gray-400"
          }
        `}
      >
        <div className="rounded-full bg-gray-100 p-3">
          <IconComponent className="h-6 w-6 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-600 text-center">{label}</p>
        <p className="text-xs text-gray-400">{hint}</p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleInputChange}
        className="hidden"
      />

      {error && (
        <p className="mt-2 text-sm text-red-500">{error}</p>
      )}

      {showPreviews && files.length > 0 && (
        <div className="mt-4 grid grid-cols-3 gap-3">
          {files.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="relative rounded-lg border border-gray-200 overflow-hidden group"
            >
              {file.type.startsWith("video/") ? (
                <div className="h-28 bg-gray-100 flex items-center justify-center">
                  <div className="flex flex-col items-center text-gray-400">
                    <svg
                      className="h-8 w-8"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                </div>
              ) : (
                <img
                  src={file.previewUrl}
                  alt={file.name}
                  className="h-28 w-full object-cover"
                />
              )}

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(index);
                }}
                className="absolute top-1.5 right-1.5 rounded-full bg-gray-800/60 p-0.5 text-white opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-3.5 w-3.5" />
              </button>

              <div className="px-2 py-1.5">
                <p className="text-xs font-medium text-gray-700 truncate">
                  {file.name}
                </p>
                <p className="text-xs text-gray-400">
                  {formatFileSize(file.size)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileDropZone;
